import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatDate, formatDateTime, API, istDate, istMonth } from "@/lib/api";
import { MapPin, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import MonthSelect from "@/components/MonthSelect";
import { StatusBadge } from "@/components/ui/AppPrimitives";

function readAttendanceView() {
  try { return JSON.parse(sessionStorage.getItem("kaya:attendance-view") || "{}"); }
  catch { return {}; }
}

export default function AttendanceTab() {
  const savedView = readAttendanceView();
  const [items, setItems] = useState([]);
  const [view, setView] = useState(savedView.view || "status");
  const [report, setReport] = useState({ employees: [] });
  const [month, setMonth] = useState(savedView.month || istMonth());
  const [employees, setEmployees] = useState([]);
  const [calendarItems, setCalendarItems] = useState([]);
  const [calendarEmployee, setCalendarEmployee] = useState("");
  const [calendarEdit, setCalendarEdit] = useState(false);
  const [calendarSaving, setCalendarSaving] = useState("");
  const [employeeWorkdays, setEmployeeWorkdays] = useState([]);
  const [officeOffDates, setOfficeOffDates] = useState(new Set());
  const [logMode, setLogMode] = useState("month");
  const [logMonth, setLogMonth] = useState(istMonth());
  const [logDay, setLogDay] = useState(istDate());
  const [todayStatus, setTodayStatus] = useState(null);
  const [statusDate, setStatusDate] = useState(istDate());
  const [calendarLeaves, setCalendarLeaves] = useState([]);
  useEffect(() => { sessionStorage.setItem("kaya:attendance-view", JSON.stringify({ view, month })); }, [view, month]);

  const loadLog = () => {
    const params = new URLSearchParams({ limit: "10000" });
    if (logMode === "day") {
      params.set("start", logDay);
      params.set("end", logDay);
    } else {
      const [year, monthNum] = logMonth.split("-").map(Number);
      const lastDay = new Date(year, monthNum, 0).getDate();
      params.set("start", `${logMonth}-01`);
      params.set("end", `${logMonth}-${String(lastDay).padStart(2, "0")}`);
    }
    return api.get(`/admin/attendance?${params.toString()}`).then((r) => setItems(r.data));
  };
  const loadReport = () => api.get(`/admin/attendance/report?month=${month}`).then(r => setReport(r.data));
  const loadTodayStatus = (dateOverride) => api.get(`/admin/attendance/status?date=${dateOverride || statusDate}`).then((r) => setTodayStatus(r.data));
  const loadCalendar = (employeeOverride, monthOverride) => {
    const employeeId = employeeOverride || calendarEmployee;
    const targetMonth = monthOverride || month;
    if (!employeeId) return Promise.resolve();
    return Promise.all([
      api.get(`/admin/attendance?employee_id=${employeeId}&start=${targetMonth}-01&end=${targetMonth}-31&limit=500`),
      api.get(`/admin/employee-workday-overrides?employee_id=${employeeId}&month=${targetMonth}`),
      api.get("/leaves"),
      loadOfficeOffDates(targetMonth),
    ]).then(([attendanceRes, workdayRes, leavesRes, offDates]) => {
      setCalendarItems(attendanceRes.data || []);
      setEmployeeWorkdays(workdayRes.data || []);
      setCalendarLeaves((leavesRes.data || []).filter((leave) => leave.employee_id === employeeId && leave.date?.startsWith(targetMonth) && ["pending", "approved"].includes(leave.status)));
      setOfficeOffDates(offDates);
    });
  };
  const loadEmployees = () => api.get("/admin/employees").then((r) => {
    setEmployees(r.data || []);
    setCalendarEmployee((v) => v || r.data?.[0]?.id || "");
  });
  useEffect(() => { loadEmployees(); }, []);
  useEffect(() => { if (view === "status") loadTodayStatus(statusDate); }, [statusDate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadLog(); }, [logMode, logMonth, logDay]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadReport();
    loadCalendar();
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (view === "calendar") loadCalendar(); }, [view, calendarEmployee]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(() => {
    if (view === "status") return loadTodayStatus(statusDate);
    if (view === "log") return loadLog();
    if (view === "calendar") return loadCalendar();
    return loadReport();
  }, [view, month, calendarEmployee, logMode, logMonth, logDay, statusDate], { enabled: !calendarEdit && !calendarSaving, intervalMs: 15000 });

  const statusStyle = {
    full_day: "bg-emerald-50 text-emerald-700 border-emerald-200",
    late: "bg-amber-50 text-amber-700 border-amber-200",
    half_day: "bg-orange-50 text-orange-700 border-orange-200",
    office_off: "bg-slate-50 text-slate-600 border-slate-200",
  };
  const label = { full_day: "Full Day", late: "Late", half_day: "Half Day", office_off: "Office Off" };

  const exportAttendance = () => {
    const url = `${API}/admin/export/attendance?month=${month}`;
    fetch(url, { credentials: "include" })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `attendance_${month}.csv`;
        a.click();
      });
  };

  const saveCalendarDay = async (date, status) => {
    if (!calendarEmployee || !date || !status) {
      toast.error("Select employee and status");
      return;
    }
    setCalendarSaving(`${date}:${status}`);
    try {
      const employeeId = calendarEmployee;
      await api.post("/admin/attendance/day", { employee_id: employeeId, date, status });
      toast.success("Attendance updated");
      await Promise.all([loadLog(), loadReport(), loadCalendar(employeeId, month)]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCalendarSaving(""); }
  };

  const setEmployeeWorkingDay = async (date, existingOverride) => {
    if (!calendarEmployee || !date) {
      toast.error("Select employee and date");
      return;
    }
    setCalendarSaving(`${date}:workday`);
    try {
      if (existingOverride?.id) {
        await api.delete(`/admin/employee-workday-overrides/${existingOverride.id}`);
        toast.success("Employee working day removed");
      } else {
        await api.put("/admin/employee-workday-overrides", {
          employee_id: calendarEmployee,
          date,
          is_working: true,
          name: "Employee working day",
        });
        toast.success("Day will count as working for this employee");
      }
      await Promise.all([loadLog(), loadReport(), loadCalendar(calendarEmployee, month)]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCalendarSaving(""); }
  };

  return (
    <div className="space-y-4" data-testid="admin-attendance">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur flex items-center justify-between flex-wrap gap-3 py-2">
        <div>
          <div className="text-overline">Team</div>
          <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Attendance</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="attendance-view-status" onClick={() => setView("status")} className={`px-4 py-2 rounded-xl text-sm ${view==="status"?"bg-[#4A5D4E] text-white":"border border-[#EAE6DF] text-[#5C635E]"}`}>Attendance Status</button>
          <button data-testid="attendance-view-report" onClick={() => setView("report")} className={`px-4 py-2 rounded-xl text-sm ${view==="report"?"bg-[#4A5D4E] text-white":"border border-[#EAE6DF] text-[#5C635E]"}`}>Report</button>
          <button data-testid="attendance-view-calendar" onClick={() => setView("calendar")} className={`px-4 py-2 rounded-xl text-sm ${view==="calendar"?"bg-[#4A5D4E] text-white":"border border-[#EAE6DF] text-[#5C635E]"}`}>Calendar</button>
          <button data-testid="attendance-view-log" onClick={() => setView("log")} className={`px-4 py-2 rounded-xl text-sm ${view==="log"?"bg-[#4A5D4E] text-white":"border border-[#EAE6DF] text-[#5C635E]"}`}>Attendance Log</button>
          {(view === "report" || view === "calendar") && (
            <>
              <MonthSelect testId="attendance-month" value={month} onChange={setMonth} />
              <button data-testid="attendance-export-csv" onClick={exportAttendance}
                className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-xl px-4 py-2 text-sm text-[#2D312E] flex items-center gap-2">
                <Download className="h-4 w-4" /> Export CSV
              </button>
            </>
          )}
        </div>
      </div>

      {view === "status" && todayStatus && <div className="card-warm overflow-hidden" data-testid="attendance-today-status">
        <div className="p-4 border-b border-[#EAE6DF] flex items-center justify-between gap-3 flex-wrap">
          <div><div className="text-overline">{statusDate === istDate() ? "Today" : "Selected date"} · {formatDate(todayStatus.date)}</div><h3 className="text-lg font-medium text-[#2D312E]">Attendance status</h3></div>
          <div className="flex items-end gap-3 flex-wrap">
            <label className="text-xs text-[#5C635E]">Attendance date<input data-testid="attendance-status-date" type="date" value={statusDate} onChange={(e) => setStatusDate(e.target.value)} className="block mt-1 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" /></label>
            <div className="flex gap-2 flex-wrap"><StatusBadge status="present" label={`Present ${todayStatus.present || 0}`} /><StatusBadge status="on_leave" label={`On leave ${todayStatus.on_leave || 0}`} /><StatusBadge status="absent" label={`Absent ${todayStatus.absent || 0}`} />{todayStatus.office_off > 0 && <StatusBadge status="office_off" label={`Office off ${todayStatus.office_off}`} />}{todayStatus.upcoming > 0 && <StatusBadge status="upcoming" label={`Upcoming ${todayStatus.upcoming}`} />}</div>
          </div>
        </div>
        <div className="overflow-x-auto"><table className="w-full min-w-[760px] text-sm" aria-label={`Attendance status for ${formatDate(todayStatus.date)}`}><thead><tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase"><th className="text-left p-3">Employee</th><th className="text-left p-3">Attendance</th><th className="text-left p-3">Detailed status</th><th className="text-left p-3">Clock in</th><th className="text-left p-3">Clock out</th><th className="text-left p-3">Office</th></tr></thead><tbody>{(todayStatus.rows || []).map((a) => <tr key={a.employee_id} className="border-t border-[#EAE6DF]"><td className="p-3 font-medium">{a.employee_name}</td><td className="p-3"><StatusBadge status={a.present_absent} /></td><td className="p-3"><StatusBadge status={a.status} label={a.status === "on_leave" && a.leave_type === "half_day" ? "Half-day leave" : undefined} title={a.leave_status ? `Leave request: ${a.leave_status}` : undefined} /></td><td className="p-3">{a.login_time ? formatDateTime(a.login_time) : "—"}</td><td className="p-3">{a.logout_time ? formatDateTime(a.logout_time) : "—"}</td><td className="p-3">{a.in_office === true ? "In office" : a.in_office === false ? "Out of office" : "—"}</td></tr>)}</tbody></table></div>
      </div>}

      {view === "status" ? null : view === "report" ? (
        <>
        <div className="card-warm overflow-hidden" data-testid="attendance-report">
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                <th className="text-left p-3">Employee</th>
                <th className="text-right p-3">Present</th>
                <th className="text-right p-3">Late</th>
                <th className="text-right p-3">Half Day</th>
                <th className="text-right p-3">On Leave</th>
                <th className="text-right p-3">Absent</th>
                <th className="text-right p-3">Late→Absent</th>
                <th className="text-right p-3">Effective Absent</th>
                <th className="text-right p-3">Hours Worked</th>
              </tr>
            </thead>
            <tbody>
              {report.employees.length === 0 ? (
                <tr><td colSpan="9" className="p-8 text-center text-[#8A938D]">No employees.</td></tr>
              ) : report.employees.map((e) => (
                <tr key={e.employee_id} data-testid={`attendance-report-row-${e.employee_id}`} className="border-t border-[#EAE6DF]">
                  <td className="p-3 font-medium text-[#2D312E]">{e.employee_name}{!e.active && <span className="ml-2 text-[10px] text-[#8A938D]">(inactive)</span>}</td>
                  <td className="p-3 text-right text-emerald-700 font-semibold">{e.present}</td>
                  <td className="p-3 text-right text-amber-700">{e.late}</td>
                  <td className="p-3 text-right text-orange-700">{e.half_day}</td>
                  <td className="p-3 text-right text-violet-700 font-semibold">{e.on_leave || 0}</td>
                  <td className="p-3 text-right text-[#C05746] font-semibold">{e.absent}</td>
                  <td className="p-3 text-right text-[#5C635E]">{e.late_to_absent}</td>
                  <td className="p-3 text-right text-[#C05746] font-semibold">{e.effective_absent}</td>
                  <td className="p-3 text-right text-[#5C635E]">{e.hours_worked}h</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        </>
      ) : view === "calendar" ? (
        <div className="card-warm p-5" data-testid="admin-attendance-calendar">
          <div className="flex items-end gap-3 flex-wrap mb-4">
            <div>
              <label className="text-xs text-[#5C635E] mb-1 block">Employee</label>
              <select value={calendarEmployee} onChange={(e) => setCalendarEmployee(e.target.value)}
                className="min-w-[220px] bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm">
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => setCalendarEdit((v) => !v)}
              className={`rounded-xl px-4 py-2 text-sm inline-flex items-center gap-2 ${calendarEdit ? "bg-[#4A5D4E] text-white" : "border border-[#EAE6DF] text-[#5C635E]"}`}>
              <Pencil className="h-3.5 w-3.5" /> {calendarEdit ? "Editing enabled" : "Enable edit"}
            </button>
          </div>
          <AttendanceCalendar
            month={month}
            items={calendarItems}
            statusStyle={statusStyle}
            label={label}
            editable={calendarEdit}
            savingKey={calendarSaving}
            onSet={saveCalendarDay}
            employeeWorkdays={employeeWorkdays}
            officeOffDates={officeOffDates}
            onToggleWorkingDay={setEmployeeWorkingDay}
            leaves={calendarLeaves}
          />
        </div>
      ) : (
        <div className="card-warm overflow-hidden">
          <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-b border-[#EAE6DF]">
            <div>
              <div className="text-overline">Attendance Log</div>
              <div className="text-sm text-[#8A938D]">Showing {items.length} records</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => setLogMode("month")}
                className={`px-4 py-2 rounded-xl text-sm ${logMode === "month" ? "bg-[#4A5D4E] text-white" : "border border-[#EAE6DF] text-[#5C635E]"}`}>
                Month
              </button>
              <button type="button" onClick={() => setLogMode("day")}
                className={`px-4 py-2 rounded-xl text-sm ${logMode === "day" ? "bg-[#4A5D4E] text-white" : "border border-[#EAE6DF] text-[#5C635E]"}`}>
                Day
              </button>
              {logMode === "month" ? (
                <MonthSelect testId="attendance-log-month" value={logMonth} onChange={setLogMonth} />
              ) : (
                <input type="date" value={logDay} onChange={(e) => setLogDay(e.target.value)}
                  className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
              )}
            </div>
          </div>
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                <th className="text-left p-3">Employee</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Marked</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-[#8A938D]">No attendance recorded yet.</td></tr>
              ) : items.map((a) => (
                <tr key={a.id} data-testid={`attendance-row-${a.id}`} className="border-t border-[#EAE6DF]">
                  <td className="p-3 font-medium text-[#2D312E]">{a.employee_name}</td>
                  <td className="p-3 text-[#5C635E]">{formatDate(a.date)}</td>
                  <td className="p-3 text-[#5C635E]">{formatDateTime(a.marked_at || a.login_time)}</td>
                  <td className="p-3 text-[#5C635E]">{a.marked_at ? "Button" : "Login"}</td>
                  <td className="p-3 text-[#5C635E]">
                    {a.login_location?.lat ? (
                      <a href={`https://www.google.com/maps?q=${a.login_location.lat},${a.login_location.lng}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#4A5D4E] hover:underline">
                        <MapPin className="h-3 w-3" /> {a.login_location.lat.toFixed(3)}, {a.login_location.lng.toFixed(3)}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="p-3"><StatusBadge status={a.leave_id ? "on_leave" : a.status} label={a.leave_id && a.leave_type === "half_day" ? "Half-day leave" : undefined} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

async function loadOfficeOffDates(month) {
  const [workingDaysRes, holidaysRes, overridesRes] = await Promise.all([
    api.get("/admin/working-days"),
    api.get(`/admin/holidays?month=${month}`),
    api.get(`/admin/workday-overrides?month=${month}`),
  ]);
  const offWeekdays = new Set((workingDaysRes.data?.off_weekdays || []).map(Number));
  const workingOverrides = new Set((overridesRes.data || [])
    .filter((item) => item.is_working)
    .map((item) => item.date));
  const dates = new Set((holidaysRes.data || []).map((item) => item.date));
  const [year, monthNum] = month.split("-").map(Number);
  const lastDay = new Date(year, monthNum, 0).getDate();
  for (let day = 1; day <= lastDay; day += 1) {
    const date = `${month}-${String(day).padStart(2, "0")}`;
    const weekday = (new Date(`${date}T00:00:00`).getDay() + 6) % 7;
    if (offWeekdays.has(weekday)) dates.add(date);
  }
  workingOverrides.forEach((date) => dates.delete(date));
  return dates;
}

function AttendanceCalendar({ month, items, statusStyle, label, editable, savingKey, onSet,
  employeeWorkdays = [], officeOffDates = new Set(), onToggleWorkingDay, leaves = [] }) {
  const byDate = Object.fromEntries((items || []).map((item) => [item.date, item]));
  const leaveByDate = Object.fromEntries((leaves || []).map((leave) => [leave.date, leave]));
  const workdayByDate = Object.fromEntries((employeeWorkdays || []).map((item) => [item.date, item]));
  const startBlank = (new Date(`${month}-01T00:00:00`).getDay() + 6) % 7;
  const [year, monthNum] = month.split("-").map(Number);
  const lastDay = new Date(year, monthNum, 0).getDate();
  const days = Array.from({ length: lastDay }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`);
  return (
    <div className="grid grid-cols-7 gap-2 text-xs">
      {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="text-center text-[#8A938D] font-semibold">{d}</div>)}
      {Array.from({ length: startBlank }).map((_, i) => <div key={`blank-${i}`} />)}
      {days.map((date) => {
        const item = byDate[date];
        const leave = leaveByDate[date];
        const employeeWorking = !!workdayByDate[date]?.is_working;
        const officeOff = officeOffDates.has(date);
        const status = leave ? "on_leave" : item?.status || (officeOff && !employeeWorking ? "office_off" : "absent");
        return (
          <div key={date} className="border border-[#EAE6DF] rounded-lg bg-white p-2 min-h-[96px]">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[#2D312E]">{Number(date.slice(8, 10))}</span>
              <StatusBadge status={status} label={leave?.leave_type === "half_day" ? "Half-day leave" : undefined} />
            </div>
            {item?.marked_at && <div className="text-[10px] text-[#8A938D] mt-1">In {formatDateTime(item.marked_at)}</div>}
            {item?.logout_time && <div className="text-[10px] text-[#8A938D]">Out {formatDateTime(item.logout_time)}</div>}
            {employeeWorking && <div className="text-[10px] text-[#C05746] mt-1">Working for this employee</div>}
            {editable && (
              <div className="grid grid-cols-3 gap-1 mt-2">
                {["full_day", "late", "half_day"].map((next) => (
                  <button key={next} type="button" onClick={() => onSet(date, next)} disabled={!!savingKey}
                    className={`border rounded px-1 py-1 text-[10px] hover:border-[#4A5D4E] disabled:opacity-60 ${status === next ? "border-[#4A5D4E] bg-[#F4F1EA] text-[#2D312E]" : "border-[#EAE6DF] text-[#5C635E]"}`}>
                    {savingKey === `${date}:${next}` ? "..." : next === "full_day" ? "Full" : next === "half_day" ? "Half" : "Late"}
                  </button>
                ))}
                <button type="button" onClick={() => onToggleWorkingDay(date, workdayByDate[date])} disabled={!!savingKey}
                  className={`col-span-3 border rounded px-1 py-1 text-[10px] disabled:opacity-60 ${employeeWorking ? "border-[#C05746] bg-red-50 text-[#C05746]" : "border-[#EAE6DF] text-[#5C635E] hover:border-[#4A5D4E]"}`}>
                  {savingKey === `${date}:workday` ? "..." : employeeWorking ? "Remove employee working day" : "Count working for employee"}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
